import { storage } from "../firebase/firebase";
import { ref, listAll, getDownloadURL } from "firebase/storage";

// ---------------------------------------------------------------------------
// The data layer that replaces every hand-pasted URL on the old site.
//
//   listFolder("Slovenia")  ->  [{ name, url, caption }, ...]
//
// Results are cached in sessionStorage, so each folder is listed at most
// once per visit (listAll is a billed class-B operation; this keeps it to
// pennies and makes back-navigation instant).
// ---------------------------------------------------------------------------

const CACHE_VERSION = "v1"; // bump to invalidate everyone's cache

function cacheKey(folder) {
  return `photolevs:${CACHE_VERSION}:${folder}`;
}

/**
 * Turn a filename into a human caption.
 *   "03_Blue-2CV_Golo-Gorge.jpg" -> "Blue 2CV, Golo Gorge"
 *   "DSCF6816.jpg"               -> "DSCF6816"  (rename exports for captions)
 */
export function captionFromName(name) {
  const stem = name.replace(/\.[a-z0-9]+$/i, "");
  const withoutOrder = stem.replace(/^\d+[-_ ]*/, "");
  const parts = withoutOrder.split("_").map((p) => p.replace(/-/g, " ").trim());
  return parts.filter(Boolean).join(", ") || stem;
}

/**
 * List every image in a Storage folder, sorted by filename (numeric-aware,
 * so 2 < 10). Returns [{ name, url, caption }].
 */
export async function listFolder(folder) {
  const key = cacheKey(folder);
  try {
    const hit = sessionStorage.getItem(key);
    if (hit) return JSON.parse(hit);
  } catch {
    /* sessionStorage unavailable (private mode) -- just fetch */
  }

  const res = await listAll(ref(storage, folder));
  const items = await Promise.all(
    res.items.map(async (item) => ({
      name: item.name,
      url: await getDownloadURL(item),
      caption: captionFromName(item.name),
    }))
  );
  items.sort((a, b) =>
    a.name.localeCompare(b.name, undefined, { numeric: true })
  );

  try {
    sessionStorage.setItem(key, JSON.stringify(items));
  } catch {
    /* quota exceeded -- fine, we just refetch next time */
  }
  return items;
}

/**
 * Accepts a full https URL or a Storage path; returns a usable URL.
 */
export async function resolveImageUrl(pathOrUrl) {
  if (!pathOrUrl) return "";
  if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
  return getDownloadURL(ref(storage, pathOrUrl));
}